import { useState, useRef } from "react";
import { X, Plus, ChevronDown, Trash2 } from "lucide-react";

export function AddRequirementForm({ onSave, onCancel }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("text");
  const [images, setImages] = useState([]); // preview urls (data:)
  const [imageFiles, setImageFiles] = useState([]); // File[]
  const fileInputRef = useRef(null);

  // Value states for different types
  const [textValue, setTextValue] = useState("");
  const [booleanValue, setBooleanValue] = useState(null);
  const [dimensionFormat, setDimensionFormat] = useState("2d");
  const [dimValue1, setDimValue1] = useState("");
  const [dimValue2, setDimValue2] = useState("");
  const [dimValue3, setDimValue3] = useState("");
  const [dimensionUnit, setDimensionUnit] = useState("ft");
  const [colorValue, setColorValue] = useState("#3B0069");

  // Select states
  const [isTypeSelectOpen, setIsTypeSelectOpen] = useState(false);
  const [isUnitSelectOpen, setIsUnitSelectOpen] = useState(false);

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImages((prev) => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
      setImageFiles((prev) => [...prev, ...Array.from(files)]);
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getValue = () => {
    switch (type) {
      case "text":
        return textValue;
      case "boolean":
        return booleanValue === null ? "" : booleanValue ? "Yes" : "No";
      case "dimensions":
        if (dimensionFormat === "linear") {
          return `${dimValue1} ${dimensionUnit}`;
        } else if (dimensionFormat === "2d") {
          return `${dimValue1}×${dimValue2} ${dimensionUnit}`;
        } else {
          return `${dimValue1}×${dimValue2}×${dimValue3} ${dimensionUnit}`;
        }
      case "color":
        return colorValue;
      default:
        return "";
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case "text":
        return "Text";
      case "boolean":
        return "Boolean";
      case "dimensions":
        return "Dimensions";
      case "color":
        return "Color";
      default:
        return "Text";
    }
  };

  const isValid = () => {
    if (!name.trim()) return false;
    switch (type) {
      case "text":
        return textValue.trim().length > 0;
      case "boolean":
        return booleanValue !== null;
      case "dimensions":
        if (dimensionFormat === "linear") return dimValue1.trim().length > 0;
        if (dimensionFormat === "2d")
          return dimValue1.trim().length > 0 && dimValue2.trim().length > 0;
        return (
          dimValue1.trim().length > 0 &&
          dimValue2.trim().length > 0 &&
          dimValue3.trim().length > 0
        );
      case "color":
        return colorValue.length > 0;
      default:
        return false;
    }
  };

  const handleSave = () => {
    if (!isValid()) return;
    onSave({
      field: name,
      type: getTypeLabel(),
      value: getValue(),
      hasPhoto: images.length > 0,
      images,
      imageFiles,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isValid()) {
      handleSave();
    }
  };

  const typeOptions = [
    { value: "text", label: "Text" },
    { value: "boolean", label: "Boolean / Choice" },
    { value: "dimensions", label: "Dimensions" },
    { value: "color", label: "Color" },
  ];

  const unitOptions = [
    { value: "ft", label: "ft" },
    { value: "in", label: "in" },
    { value: "cm", label: "cm" },
    { value: "m", label: "m" },
  ];

  return (
    <div
      className="border border-gray-200 rounded-lg bg-white shadow-sm p-4 animate-in slide-in-from-top-2 duration-200"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-900">
          Add Requirement
        </h4>
        <button
          className="h-6 w-6 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
          onClick={onCancel}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Image Upload */}
      <div className="mb-4">
        <label className="text-xs font-medium text-gray-500 mb-2 block">
          Add Image (Optional)
        </label>
        <div className="flex items-center gap-2">
          {images.map((img, index) => (
            <div
              key={index}
              className="relative w-16 h-16 rounded-lg border border-gray-200 overflow-hidden group"
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(index)}
                className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {images.length < 2 && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-200 hover:border-sm-primary/50 flex items-center justify-center text-gray-400 hover:text-sm-primary transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleImageUpload}
          />
        </div>
      </div>

      {/* Name Field */}
      <div className="mb-3">
        <label className="text-xs font-medium text-gray-500 mb-1.5 block">
          Requirement Name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Lighting, Storage Type, Flooring Finish"
          className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
        />
      </div>

      {/* Type Selector */}
      <div className="mb-3 relative">
        <label className="text-xs font-medium text-gray-500 mb-1.5 block">
          Type
        </label>
        <button
          onClick={() => setIsTypeSelectOpen(!isTypeSelectOpen)}
          className="w-full h-9 px-3 text-sm flex items-center justify-between border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-sm-primary"
        >
          <span>{typeOptions.find(opt => opt.value === type)?.label}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isTypeSelectOpen ? "rotate-180" : ""}`} />
        </button>
        {isTypeSelectOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1">
            {typeOptions.map((opt) => (
              <button
                key={opt.value}
                className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${type === opt.value ? "text-sm-primary font-medium bg-sm-primary/5" : "text-gray-700"
                  }`}
                onClick={() => {
                  setType(opt.value);
                  setIsTypeSelectOpen(false);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Dynamic Value Field */}
      <div className="mb-4">
        <label className="text-xs font-medium text-gray-500 mb-1.5 block">
          Value
        </label>

        {type === "text" && (
          <input
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            placeholder="Enter value..."
            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
          />
        )}

        {type === "boolean" && (
          <div className="flex gap-2">
            <button
              onClick={() => setBooleanValue(true)}
              className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors border ${booleanValue === true
                  ? "bg-sm-primary text-white border-sm-primary"
                  : "bg-white text-gray-500 border-gray-300 hover:border-sm-primary/50"
                }`}
            >
              Yes
            </button>
            <button
              onClick={() => setBooleanValue(false)}
              className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors border ${booleanValue === false
                  ? "bg-sm-primary text-white border-sm-primary"
                  : "bg-white text-gray-500 border-gray-300 hover:border-sm-primary/50"
                }`}
            >
              No
            </button>
          </div>
        )}

        {type === "dimensions" && (
          <div className="space-y-2">
            {/* Dimension Format Selector */}
            <div className="flex gap-1">
              {["linear", "2d", "3d"].map((format) => (
                <button
                  key={format}
                  onClick={() => setDimensionFormat(format)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors border ${dimensionFormat === format
                      ? "bg-sm-primary text-white border-sm-primary"
                      : "bg-white text-gray-500 border-gray-300 hover:border-sm-primary/50"
                    }`}
                >
                  {format === "linear" ? "Linear" : format.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Dimension Inputs */}
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={dimValue1}
                onChange={(e) => setDimValue1(e.target.value)}
                placeholder="0"
                className="h-9 text-sm w-20 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
              />
              {(dimensionFormat === "2d" || dimensionFormat === "3d") && (
                <>
                  <span className="text-gray-400">×</span>
                  <input
                    type="number"
                    value={dimValue2}
                    onChange={(e) => setDimValue2(e.target.value)}
                    placeholder="0"
                    className="h-9 text-sm w-20 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  />
                </>
              )}
              {dimensionFormat === "3d" && (
                <>
                  <span className="text-gray-400">×</span>
                  <input
                    type="number"
                    value={dimValue3}
                    onChange={(e) => setDimValue3(e.target.value)}
                    placeholder="0"
                    className="h-9 text-sm w-20 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  />
                </>
              )}
              <div className="relative w-20">
                <button
                  onClick={() => setIsUnitSelectOpen(!isUnitSelectOpen)}
                  className="w-full h-9 px-2 text-sm flex items-center justify-between border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-sm-primary"
                >
                  <span>{dimensionUnit}</span>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </button>
                {isUnitSelectOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 bottom-full mb-1">
                    {unitOptions.map((opt) => (
                      <button
                        key={opt.value}
                        className={`w-full px-2 py-1.5 text-xs text-left hover:bg-gray-50 transition-colors ${dimensionUnit === opt.value ? "text-sm-primary font-medium bg-sm-primary/5" : "text-gray-700"
                          }`}
                        onClick={() => {
                          setDimensionUnit(opt.value);
                          setIsUnitSelectOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {type === "color" && (
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-md border border-gray-200 overflow-hidden cursor-pointer relative"
              style={{ backgroundColor: colorValue }}
            >
              <input
                type="color"
                value={colorValue}
                onChange={(e) => setColorValue(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
            </div>
            <input
              value={colorValue}
              onChange={(e) => setColorValue(e.target.value)}
              placeholder="#3B0069"
              className="h-9 px-3 text-sm flex-1 font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
            />
            <div
              className="w-9 h-9 rounded-md border border-gray-200"
              style={{ backgroundColor: colorValue }}
            />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="h-8 px-3 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!isValid()}
          className={`h-8 px-3 text-xs font-medium text-white rounded-md transition-colors ${isValid() ? "bg-sm-primary hover:bg-sm-hover" : "bg-gray-300 cursor-not-allowed"
            }`}
        >
          Save Requirement
        </button>
      </div>
    </div>
  );
}
